import { useContext } from 'react';
import { useMsal } from '@azure/msal-react';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import StripeComponent from 'src/components/Checkout/Stripe';
import { AuthorizationContext } from 'src/context/AuthorizationProvider';
import useCRUD from 'src/hooks/useCRUD';
import { formatCurrency, getDueDateStatus } from 'src/lib/util/currency-utils';

const InvoiceDetail = () => {
  const { customerId } = useContext(AuthorizationContext);
  const location = useLocation();
  const { accounts } = useMsal();
  const params = useParams();
  const { t } = useTranslation('user');
  let { connectedAccountNumber } = location.state || {};
  const userId = accounts[0]?.idTokenClaims?.sub;
  const { data } = useCRUD(`${process.env.REACT_APP_API_URL}/property/${customerId}/user/${userId}/invoice/${params.id}`);
  const { accountNumber, invoice, successInd } = data || {};
  if (!connectedAccountNumber) connectedAccountNumber = accountNumber;
  if (!successInd) return null;
  if (!invoice) return 'Loading...';
  if (!connectedAccountNumber) return 'No connected account number found';

  // Parse items if needed
  let items = [];
  if (invoice.items) {
    if (typeof invoice.items === 'string') {
      try {
        items = JSON.parse(invoice.items);
      } catch {
        items = [];
      }
    } else if (Array.isArray(invoice.items)) {
      items = invoice.items;
    }
  }
  const dueStatus = getDueDateStatus(invoice.dueDate, invoice.paid, t);
  const showBalance = !invoice.paid && invoice.amountPaid > 0;
  const showPenalty = invoice.penaltyApplied;

  return (
    <Box bgcolor="background.paper" borderRadius={2} boxShadow={2} maxWidth={600} mt={4} mx="auto" p={3}>
      <Stack alignItems="center" direction="row" justifyContent="space-between" mb={2}>
        <Typography variant="h5">{formatCurrency(invoice.amountDue)}</Typography>
        <Chip color={dueStatus.color} label={dueStatus.text} variant={dueStatus.color === 'default' ? 'outlined' : 'filled'} />
      </Stack>
      <Divider sx={{ mb: 2 }} />
      <Stack spacing={2}>
        <Stack direction="row" spacing={2}>
          <Typography variant="subtitle2">{t('invoices.fields.invoiceId')}:</Typography>
          <Typography>{invoice.invoiceId}</Typography>
        </Stack>
        {invoice.billingPeriod && (
          <Stack direction="row" spacing={2}>
            <Typography variant="subtitle2">{t('invoices.fields.billingPeriod') || 'Billing Period'}:</Typography>
            <Typography>{invoice.billingPeriod}</Typography>
          </Stack>
        )}
        {invoice.dueDate && (
          <Stack direction="row" spacing={2}>
            <Typography variant="subtitle2">{t('invoices.fields.dueDate') || 'Due Date'}:</Typography>
            <Typography>{new Date(invoice.dueDate).toLocaleDateString()}</Typography>
          </Stack>
        )}
        <Stack direction="row" spacing={2}>
          <Typography variant="subtitle2">{t('invoices.fields.amountDue')}:</Typography>
          <Typography>{formatCurrency(invoice.amountDue)}</Typography>
        </Stack>
        {showPenalty && (
          <Typography color="error" variant="body2">
            {t('invoices.penalty')}
          </Typography>
        )}
        {(invoice.paid || invoice.amountPaid > 0) && (
          <Stack direction="row" spacing={2}>
            <Typography variant="subtitle2">{t('invoices.fields.amountPaid')}:</Typography>
            <Typography>{formatCurrency(invoice.amountPaid)}</Typography>
          </Stack>
        )}
        {showBalance && (
          <Stack direction="row" spacing={2}>
            <Typography variant="subtitle2">{t('invoices.fields.balance') || 'Balance'}:</Typography>
            <Typography>{formatCurrency(invoice.amountDue - invoice.amountPaid)}</Typography>
          </Stack>
        )}
        <Box>
          <Typography color="text.primary" mb={1} variant="subtitle2">
            {t('invoices.fields.items') || 'Items'}:
          </Typography>
          {items.length > 0 ? (
            <Stack spacing={1}>
              {items.map((item, idx) => (
                <Box
                  borderRadius={1}
                  key={item.paymentCatalogId || idx}
                  p={1}
                  sx={{
                    bgcolor: theme => (theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100')
                  }}
                >
                  <Typography color="text.primary" fontWeight="bold">
                    {item.name}
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    {t('invoices.fields.itemAmount', { amount: formatCurrency(Number(item.amount)) })}
                  </Typography>
                  {item.notes && (
                    <Typography color="text.secondary" variant="body2">
                      {t('invoices.fields.itemNotes', { notes: item.notes })}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2">{t('invoices.noItems')}</Typography>
          )}
        </Box>
        {!invoice.paid && (
          <StripeComponent connectedAccountNumber={connectedAccountNumber} customerId={customerId} invoice={invoice} userId={userId} />
        )}
      </Stack>
    </Box>
  );
};

export default InvoiceDetail;
